import { findSeatBlock } from 'customer-flows/utils/seat-maps/seat-block'

export default function seatMaps({ stores }: CommandArgs) {
  const seatMapsStore = stores['seat-maps'] as SeatMapsStore

  /**
   * updates the seat map selection for a given map, merging with existing selections
   * @param seatMapId
   * @param seats
   */
  function updateMapSelection(seatMapId: string, seats: Record<string, string>) {
    seatMapsStore.selections = { ...seatMapsStore.selections, [seatMapId]: seats }
  }

  /**
   * kept seats retain their ticket, new ones get the first allowed ticket, seats with none or no longer selectable are dropped
   * @param seatMap
   * @param blockIds
   */
  function setSeats(seatMap: SeatMapEntry, blockIds: string[]) {
    const currentSeats = seatMapsStore.selections[seatMap.id] ?? {}
    const nextSeats: Record<string, string> = {}
    const selectableBlockIds = new Set(seatMapsStore.getSelectableBlocks(seatMap).map((block) => block.id))

    for (const blockId of blockIds) {
      const match = findSeatBlock(seatMap.mapData, blockId)
      if (!match || !selectableBlockIds.has(blockId)) continue

      const ticketOptions = seatMapsStore.getTicketOptionsForBlock(match.block)
      const currentTicketId = currentSeats[blockId]
      const keepsCurrentTicket = ticketOptions.some((option) => option.id === currentTicketId)
      const ticketId = keepsCurrentTicket ? currentTicketId : ticketOptions[0]?.id

      if (ticketId) nextSeats[blockId] = ticketId
    }

    updateMapSelection(seatMap.id, nextSeats)
  }

  /**
   * updates the ticket for a given seat, if it is currently selected
   * @param seatMapId
   * @param blockId
   * @param ticketId
   */
  function setSeatTicket(seatMapId: string, blockId: string, ticketId: string) {
    const currentSeats = seatMapsStore.selections[seatMapId]
    if (!currentSeats?.[blockId]) return

    updateMapSelection(seatMapId, { ...currentSeats, [blockId]: ticketId })
  }

  /**
   * removes a seat from the selection for a given map
   * @param seatMapId
   * @param blockId
   */
  function removeSeat(seatMapId: string, blockId: string) {
    const { [blockId]: _removed, ...remainingSeats } = seatMapsStore.selections[seatMapId] ?? {}

    updateMapSelection(seatMapId, remainingSeats)
  }

  return {
    setSeats,
    setSeatTicket,
    removeSeat
  }
}

declare global {
  export type SeatMapsCommands = ReturnType<typeof seatMaps>

  interface CommandRegistry {
    'seat-maps': SeatMapsCommands
  }
}
